import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import TempleCard from '../components/TempleCard';
import AdBanner from '../components/AdBanner';
import { getTemples } from '../services/api';
import useSEO from "../hooks/useSEO";

const Home = () => {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  useSEO("home");
  useEffect(() => {
    const fetchTemples = async () => {
      const data = await getTemples();
      setTemples(data);
      setLoading(false);
    };
    fetchTemples();
  }, []);

  const filteredTemples = temples.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/HeroBanner_nath-nagri-bareilly-com.png"
            alt="Nathnagri Temples Hero Banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60 mix-blend-multiply"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight drop-shadow-lg"
          >
            <span className="text-saffron-500">नाथ नगरी</span> दर्शन
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-stone-200 mb-10 font-light"
          >
            भगवान शिव के उन सात प्राचीन मंदिरों का अन्वेषण करें जो बरेली के आध्यात्मिक हृदय को परिभाषित करते हैं।
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link
              to="/temples"
              className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-saffron-600 hover:bg-saffron-700 shadow-xl shadow-saffron-600/30 transition-all hover:scale-105"
            >
              मंदिर देखें <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">

        {/* Banner Ad */}
        <AdBanner placement="banner" />

        {/* Header & Search */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 space-y-6 md:space-y-0 mt-8">
          <div>
            <h2 className="text-3xl font-bold text-stone-800 mb-3 tracking-tight">प्रमुख मंदिर</h2>
            <p className="text-stone-500 max-w-2xl">उन प्रमुख स्थानों की खोज करें जो देश भर से भक्तों को आकर्षित करते हैं।</p>
          </div>

          <div className="relative max-w-md w-full">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-stone-400" />
            </div>
            <input
              type="text"
              placeholder="मंदिर खोजें..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-full text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:border-transparent transition-shadow shadow-sm"
            />
          </div>
        </div>

        {/* Loading / Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-2xl h-[400px] border border-stone-100 shadow-sm animate-pulse flex flex-col">
                <div className="h-56 bg-stone-200"></div>
                <div className="p-5 flex-1 space-y-3">
                  <div className="h-6 bg-stone-200 rounded w-1/3"></div>
                  <div className="h-4 bg-stone-200 rounded w-1/4"></div>
                  <div className="space-y-2 mt-4 text-sm text-stone-600">
                    <div className="h-4 bg-stone-200 rounded w-full"></div>
                    <div className="h-4 bg-stone-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTemples.slice(0, 3).map((temple, index) => (
              <TempleCard key={temple.id} temple={temple} index={index} />
            ))}
          </div>
        )}

        {filteredTemples.length === 0 && !loading && (
          <div className="text-center py-20 bg-white rounded-3xl border border-stone-100 shadow-sm">
            <h3 className="text-xl text-stone-600">"{searchTerm}" से मेल खाने वाला कोई मंदिर नहीं मिला</h3>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-saffron-600 hover:text-saffron-700 font-medium"
            >
              खोज साफ़ करें
            </button>
          </div>
        )}

      </section>
    </div>
  );
};

export default Home;
